import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";

type Ann = {
  id: string; title: string; message: string; severity: string;
  is_active: boolean; starts_at: string; ends_at: string | null; created_at: string;
};

export const AnnouncementsTab = () => {
  const { toast } = useToast();
  const [items, setItems] = useState<Ann[]>([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await supabase.from("system_announcements").select("*").order("created_at", { ascending: false });
    setItems((data as Ann[]) || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim() || !message.trim()) return;
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("system_announcements").insert({
      title, message, severity, created_by: user!.id,
    });
    setLoading(false);
    if (error) { toast({ title: "Failed", description: error.message, variant: "destructive" }); return; }
    setTitle(""); setMessage(""); setSeverity("info");
    toast({ title: "Announcement published" });
    load();
  };

  const toggle = async (id: string, is_active: boolean) => {
    await supabase.from("system_announcements").update({ is_active }).eq("id", id);
    load();
  };

  const remove = async (id: string) => {
    await supabase.from("system_announcements").delete().eq("id", id);
    load();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Publish announcement</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)} />
          <div className="flex gap-3">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={create} disabled={loading}>Publish</Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {items.map((a) => (
          <Card key={a.id}>
            <CardContent className="flex items-start justify-between gap-4 pt-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={a.severity === "critical" ? "destructive" : "secondary"}>{a.severity}</Badge>
                  <h4 className="font-semibold">{a.title}</h4>
                </div>
                <p className="text-sm text-muted-foreground">{a.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{new Date(a.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={a.is_active} onCheckedChange={(v) => toggle(a.id, v)} />
                <Button variant="ghost" size="icon" onClick={() => remove(a.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No announcements yet.</p>}
      </div>
    </div>
  );
};
